/**
 * Customer Chat Widget
 * Floating chat button + chat window for customer site
 */

import {
  AttachFile as AttachIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  Done as DoneIcon,
  InsertDriveFile as FileIcon,
  OpenInFull as MaximizeIcon,
  Minimize as MinimizeIcon,
  Send as SendIcon,
  SupportAgent as SupportIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Fab,
  IconButton,
  Paper,
  Rating,
  Slide,
  TextField,
  Typography,
  Zoom,
} from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

// API base URL - use same env variable as rest of client
const API_URL = import.meta.env.VITE_API_BASE_URL || '';

// Chat service
class ChatService {
  constructor() {
    this.token = localStorage.getItem('customer_chat_token');
    this.sessionId = localStorage.getItem('customer_chat_session');
    this.chatId = localStorage.getItem('customer_chat_id');
  }

  getHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(this.token && { 'X-Customer-Token': this.token }),
    };
  }

  async initSession(data = {}) {
    const res = await fetch(`${API_URL}/api/customer-chat/init`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        referrer: document.referrer,
        landingPage: window.location.href,
        ...data,
      }),
    });
    const result = await res.json();

    if (result.token) {
      this.token = result.token;
      this.sessionId = result.sessionId;
      localStorage.setItem('customer_chat_token', result.token);
      localStorage.setItem('customer_chat_session', result.sessionId);
      if (result.chatId) {
        this.chatId = result.chatId;
        localStorage.setItem('customer_chat_id', result.chatId);
      }
    }

    return result;
  }

  async startChat(data) {
    const res = await fetch(`${API_URL}/api/customer-chat/start`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    const result = await res.json();

    if (result.chatId) {
      this.chatId = result.chatId;
      localStorage.setItem('customer_chat_id', result.chatId);
    }

    return result;
  }

  async getMessages(before) {
    const params = before ? `?before=${before}` : '';
    const res = await fetch(
      `${API_URL}/api/customer-chat/${this.chatId}/messages${params}`,
      {
        headers: this.getHeaders(),
      }
    );
    return res.json();
  }

  async sendMessage(content, replyToId) {
    const res = await fetch(
      `${API_URL}/api/customer-chat/${this.chatId}/messages`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ content, replyToId }),
      }
    );
    return res.json();
  }

  async getChatInfo() {
    const res = await fetch(
      `${API_URL}/api/customer-chat/${this.chatId}/info`,
      {
        headers: this.getHeaders(),
      }
    );
    return res.json();
  }

  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(
      `${API_URL}/api/customer-chat/${this.chatId}/upload`,
      {
        method: 'POST',
        headers: { 'X-Customer-Token': this.token },
        body: formData,
      }
    );
    return res.json();
  }

  async sendTyping() {
    await fetch(`${API_URL}/api/customer-chat/${this.chatId}/typing`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
  }

  async submitRating(rating, feedback) {
    const res = await fetch(
      `${API_URL}/api/customer-chat/${this.chatId}/rating`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ rating, feedback }),
      }
    );
    return res.json();
  }

  async pollMessages(since) {
    const res = await fetch(
      `${API_URL}/api/customer-chat/${this.chatId}/poll?since=${since}`,
      {
        headers: this.getHeaders(),
      }
    );
    return res.json();
  }

  get hasSession() {
    return !!this.token;
  }

  get hasChat() {
    return !!this.chatId;
  }
}

const chatService = new ChatService();

// Message component
function ChatMessage({ message, isOwn }) {
  const formatTime = (timestamp) => {
    return format(new Date(timestamp * 1000), 'HH:mm');
  };

  const isSystem = message.senderType === 'system';

  if (isSystem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
        <Typography
          variant="caption"
          sx={{
            bgcolor: 'grey.100',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            color: 'text.secondary',
          }}
        >
          {message.content}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 1,
        mb: 1,
        px: 1,
      }}
    >
      {!isOwn && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'primary.main',
            fontSize: 12,
          }}
        >
          {message.senderName?.charAt(0) || (
            <SupportIcon sx={{ fontSize: 16 }} />
          )}
        </Avatar>
      )}

      <Box sx={{ maxWidth: '75%' }}>
        {!isOwn && message.senderName && (
          <Typography
            variant="caption"
            color="primary"
            sx={{ ml: 1, fontWeight: 600 }}
          >
            {message.senderName}
          </Typography>
        )}

        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: isOwn ? 'primary.main' : 'grey.100',
            color: isOwn ? 'white' : 'text.primary',
            borderRadius: 2,
            borderTopLeftRadius: isOwn ? 16 : 4,
            borderTopRightRadius: isOwn ? 4 : 16,
          }}
        >
          {/* Attachments */}
          {message.attachments?.length > 0 && (
            <Box sx={{ mb: message.content ? 1 : 0 }}>
              {message.attachments.map((att, idx) => (
                <Box
                  key={idx}
                  component="a"
                  href={att.fileUrl}
                  target="_blank"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    bgcolor: isOwn
                      ? 'rgba(255,255,255,0.1)'
                      : 'background.paper',
                    borderRadius: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  {att.fileType?.startsWith('image/') ? (
                    <Box
                      component="img"
                      src={att.thumbnailUrl || att.fileUrl}
                      alt={att.fileName}
                      sx={{ maxWidth: 200, maxHeight: 150, borderRadius: 1 }}
                    />
                  ) : (
                    <>
                      <FileIcon fontSize="small" />
                      <Typography
                        variant="body2"
                        sx={{
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word',
                        }}
                      >
                        {att.fileName}
                      </Typography>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Text */}
          {message.content && (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {message.content}
            </Typography>
          )}

          {/* Time and status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: 10 }}>
              {formatTime(message.createdAt)}
            </Typography>
            {isOwn &&
              (message.status === 'read' ? (
                <DoneAllIcon
                  sx={{ fontSize: 14, opacity: 0.7, color: '#4fc3f7' }}
                />
              ) : message.status === 'delivered' ? (
                <DoneAllIcon sx={{ fontSize: 14, opacity: 0.7 }} />
              ) : (
                <DoneIcon sx={{ fontSize: 14, opacity: 0.7 }} />
              ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

// Pre-chat form
function PreChatForm({ onSubmit, loading, orderId }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ name, email, phone, orderId, initialMessage: message });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Начать чат
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Представьтесь, чтобы менеджер мог связаться с вами
      </Typography>

      <TextField
        id="chat-name"
        name="name"
        autoComplete="name"
        fullWidth
        size="small"
        label="Ваше имя"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        id="chat-phone"
        name="phone"
        autoComplete="tel"
        fullWidth
        size="small"
        label="Телефон"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+996 XXX XXX XXX"
        sx={{ mb: 2 }}
      />

      <TextField
        id="chat-email"
        name="email"
        autoComplete="email"
        fullWidth
        size="small"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        sx={{ mb: 2 }}
      />

      <TextField
        id="chat-message"
        name="message"
        fullWidth
        size="small"
        label="Ваш вопрос"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        multiline
        rows={3}
        placeholder="Опишите ваш вопрос..."
        sx={{ mb: 2 }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || !name.trim()}
        startIcon={loading ? <CircularProgress size={16} /> : <ChatIcon />}
      >
        {loading ? 'Подключение...' : 'Начать чат'}
      </Button>
    </Box>
  );
}

// Rating form
function RatingForm({ onSubmit, loading }) {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (rating > 0) {
      onSubmit(rating, feedback);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ p: 2, textAlign: 'center' }}
    >
      <SupportIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
      <Typography variant="h6" gutterBottom>
        Оцените наш сервис
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Ваш отзыв поможет нам стать лучше
      </Typography>

      <Rating
        id="chat-rating"
        name="chatRating"
        value={rating}
        onChange={(_, value) => setRating(value)}
        size="large"
        sx={{ mb: 2 }}
      />

      <TextField
        id="chat-feedback"
        name="feedback"
        fullWidth
        size="small"
        label="Комментарий (необязательно)"
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || rating === 0}
      >
        {loading ? 'Отправка...' : 'Отправить оценку'}
      </Button>
    </Box>
  );
}

// Main Chat Widget
export default function ChatWidget({
  orderId,
  _position = 'bottom-right', // kept for API compatibility
  primaryColor = '#1976d2',
  companyName = 'Garan Asia',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [chatInfo, setChatInfo] = useState(null);
  const [, setHasSession] = useState(chatService.hasSession);
  const [hasChat, setHasChat] = useState(chatService.hasChat);
  const [managerTyping, setManagerTyping] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageTime = useRef(0);
  const pollInterval = useRef(null);
  const typingTimeout = useRef(null);

  // Load chat data helper
  const loadChatData = useCallback(async () => {
    try {
      setLoading(true);
      const [msgsRes, infoRes] = await Promise.all([
        chatService.getMessages(),
        chatService.getChatInfo(),
      ]);

      if (msgsRes.messages) {
        setMessages(msgsRes.messages);
        if (msgsRes.messages.length > 0) {
          lastMessageTime.current =
            msgsRes.messages[msgsRes.messages.length - 1].createdAt;
        }
      }

      if (infoRes) {
        setChatInfo(infoRes);
        setUnreadCount(infoRes.unreadCount || 0);

        // Show rating form if chat is resolved and not yet rated
        if (infoRes.status === 'resolved' && !infoRes.rating) {
          setShowRating(true);
        }
      }
    } catch (error) {
      console.error('Load chat error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling functions - MUST be defined before useEffect that uses them
  const stopPolling = useCallback(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollInterval.current) return;

    pollInterval.current = setInterval(async () => {
      try {
        const res = await chatService.pollMessages(lastMessageTime.current);

        if (res.messages?.length > 0) {
          setMessages((prev) => [...prev, ...res.messages]);
          lastMessageTime.current =
            res.messages[res.messages.length - 1].createdAt;

          // Count unread from manager
          const newUnread = res.messages.filter(
            (m) => m.senderType === 'manager'
          ).length;
          if (newUnread > 0 && !isOpen) {
            setUnreadCount((prev) => prev + newUnread);
          }
        }

        setManagerTyping(res.managerTyping);

        // Update chat status
        if (res.chatStatus) {
          setChatInfo((prev) => {
            if (prev?.status !== res.chatStatus) {
              if (res.chatStatus === 'resolved') {
                setShowRating(true);
              }
              return { ...prev, status: res.chatStatus };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 3000);
  }, [isOpen]);

  // Initialize session on mount
  useEffect(() => {
    const init = async () => {
      if (!chatService.hasSession) {
        await chatService.initSession({ orderId });
        setHasSession(chatService.hasSession);
        setHasChat(chatService.hasChat);
      }

      if (chatService.hasChat) {
        loadChatData();
      }
    };
    init();

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [orderId, loadChatData]);

  // Start polling when chat is open
  useEffect(() => {
    if (isOpen && hasChat && !isMinimized) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [isOpen, hasChat, isMinimized, startPolling, stopPolling]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current && !isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  const handleStartChat = async (data) => {
    try {
      setLoading(true);
      await chatService.startChat(data);
      setHasChat(true);
      await loadChatData();
    } catch (error) {
      console.error('Start chat error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    const content = inputValue.trim();
    if (!content || sending) return;

    try {
      setSending(true);
      setInputValue('');

      // Optimistic update
      const tempId = `temp-${Date.now()}`;
      const tempMessage = {
        id: tempId,
        content,
        senderType: 'customer',
        createdAt: Math.floor(Date.now() / 1000),
        status: 'sending',
      };
      setMessages((prev) => [...prev, tempMessage]);

      const result = await chatService.sendMessage(content);

      // Replace temp message with real one
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...result, status: 'sent' } : m))
      );

      lastMessageTime.current = result.createdAt;
    } catch (error) {
      console.error('Send message error:', error);
      // Mark message as failed
      setMessages((prev) =>
        prev.map((m) =>
          m.id?.startsWith('temp-') ? { ...m, status: 'failed' } : m
        )
      );
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSending(true);
      const result = await chatService.uploadFile(file);

      if (result.messageId) {
        await loadChatData();
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setSending(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleTyping = () => {
    if (typingTimeout.current) {
      clearTimeout(typingTimeout.current);
    }

    chatService.sendTyping();

    typingTimeout.current = setTimeout(() => {
      typingTimeout.current = null;
    }, 2000);
  };

  const handleSubmitRating = async (rating, feedback) => {
    try {
      setLoading(true);
      await chatService.submitRating(rating, feedback);
      setShowRating(false);
      setChatInfo((prev) => ({ ...prev, rating }));
    } catch (error) {
      console.error('Submit rating error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(0);
  };

  return (
    <>
      {/* Chat Button */}
      <Zoom in={!isOpen}>
        <Fab
          color="primary"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            // Responsive positioning - above mobile nav, below WhatsApp
            bottom: { xs: 76, sm: 20 }, // 64px nav + 12px spacing on mobile
            right: { xs: 16, sm: 20 },
            bgcolor: primaryColor,
            '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.9)' },
            zIndex: 1300,
          }}
        >
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : null}
            color="error"
          >
            <ChatIcon />
          </Badge>
        </Fab>
      </Zoom>

      {/* Chat Window */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            // Responsive positioning and sizing
            bottom: { xs: 0, sm: 20 },
            right: { xs: 0, sm: 20 },
            left: { xs: 0, sm: 'auto' },
            width: { xs: '100%', sm: isMinimized ? 300 : 360 },
            height: { xs: isMinimized ? 56 : 'calc(100vh - 56px)', sm: isMinimized ? 56 : 500 },
            maxHeight: { xs: 'calc(100vh - 56px)', sm: '80vh' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: { xs: '16px 16px 0 0', sm: 2 },
            zIndex: 1300,
            transition: 'all 0.3s ease',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              bgcolor: primaryColor,
              color: 'white',
              cursor: 'pointer',
            }}
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <Avatar
              sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.2)' }}
            >
              <SupportIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {companyName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {chatInfo?.manager
                  ? `${chatInfo.manager.name} — менеджер`
                  : managerTyping
                    ? 'печатает...'
                    : 'Онлайн поддержка'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={isMinimized ? 'Развернуть' : 'Свернуть'}
              sx={{ color: 'white', minWidth: 44, minHeight: 44 }}
            >
              {isMinimized ? <MaximizeIcon /> : <MinimizeIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              aria-label="Закрыть чат"
              sx={{ color: 'white', minWidth: 44, minHeight: 44 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Collapse in={!isMinimized}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: 444,
              }}
            >
              {/* Show pre-chat form if no chat started */}
              {!hasChat ? (
                <PreChatForm
                  onSubmit={handleStartChat}
                  loading={loading}
                  orderId={orderId}
                />
              ) : showRating ? (
                <RatingForm onSubmit={handleSubmitRating} loading={loading} />
              ) : (
                <>
                  {/* Messages */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'auto',
                      py: 1,
                      bgcolor: 'grey.50',
                    }}
                  >
                    {loading ? (
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : messages.length === 0 ? (
                      <Box
                        sx={{
                          textAlign: 'center',
                          p: 3,
                          color: 'text.secondary',
                        }}
                      >
                        <ChatIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography variant="body2">
                          Напишите ваш вопрос
                        </Typography>
                      </Box>
                    ) : (
                      messages.map((msg) => (
                        <ChatMessage
                          key={msg.id}
                          message={msg}
                          isOwn={msg.senderType === 'customer'}
                        />
                      ))
                    )}

                    {/* Typing indicator */}
                    {managerTyping && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: 'primary.main',
                          }}
                        >
                          <SupportIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          печатает...
                        </Typography>
                      </Box>
                    )}

                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Input */}
                  <Box
                    sx={{
                      p: 1,
                      borderTop: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleFileUpload}
                      />
                      <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        aria-label="Прикрепить файл"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <AttachIcon />
                      </IconButton>

                      <TextField
                        id="chat-input"
                        name="chatMessage"
                        autoComplete="off"
                        fullWidth
                        size="small"
                        placeholder="Введите сообщение..."
                        value={inputValue}
                        onChange={(e) => {
                          setInputValue(e.target.value);
                          handleTyping();
                        }}
                        onKeyPress={(e) =>
                          e.key === 'Enter' &&
                          !e.shiftKey &&
                          handleSendMessage()
                        }
                        multiline
                        maxRows={3}
                        disabled={sending}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />

                      <IconButton
                        color="primary"
                        onClick={handleSendMessage}
                        disabled={!inputValue.trim() || sending}
                        aria-label="Отправить"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        {sending ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SendIcon />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Slide>
    </>
  );
}
